import type {
  ProvisioningRun,
  ProvisioningRunStatus,
  ProvisioningStepStatus,
  TenantAiStatus,
} from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  keys,
  platformApi,
  provisioningQuery,
  tenantAiQuery,
  tenantCopySummaryQuery,
} from '../api/platform';
import { he } from '../i18n/he';
import { waitsForContent } from '../lib/ai';
import { errorMessage } from '../lib/format';
import { Alert, Badge, Button, Card, formatDateTime, Loading } from '@avrahami/ui';
import { CopyProgress } from './AiMeters';

const runTones = {
  PENDING: 'slate',
  RUNNING: 'blue',
  WAITING: 'amber',
  FAILED: 'red',
  SUCCEEDED: 'green',
} as const satisfies Record<ProvisioningRunStatus, string>;

const stepTones = {
  PENDING: 'slate',
  RUNNING: 'blue',
  WAITING: 'amber',
  FAILED: 'red',
  SUCCEEDED: 'green',
} as const satisfies Record<ProvisioningStepStatus, string>;

/**
 * The store's provisioning run: its steps, counts and errors, and "retry" when it stopped. While
 * the run waits for the store's copy, staff who may see the content engine also see how far the
 * writing is, and staff who manage it can stop the writing and continue it from here.
 */
export function ProvisioningCard({
  tenantId,
  canRetry,
  showCopyProgress,
  canStopCopy,
}: {
  tenantId: string;
  canRetry: boolean;
  showCopyProgress: boolean;
  canStopCopy: boolean;
}) {
  const provisioning = useQuery(provisioningQuery(tenantId));
  const queryClient = useQueryClient();
  const status = provisioning.data?.run?.status;
  const previous = useRef(status);
  // The same query as the store's content card (which reports its errors); it polls by itself
  // while the store has open content jobs.
  const content = useQuery({
    ...tenantAiQuery(tenantId),
    enabled: showCopyProgress && waitsForContent(provisioning.data?.run),
  });
  // The same summary as the copy screen: whether the writing is stopped, for the stop button.
  const copySummary = useQuery({
    ...tenantCopySummaryQuery(tenantId),
    enabled: canStopCopy && waitsForContent(provisioning.data?.run),
  });

  // The run moves the tenant (PROVISIONING → ACTIVE), starts the content jobs and writes audit
  // rows: refresh those views whenever its status changes.
  useEffect(() => {
    if (previous.current !== undefined && previous.current !== status) {
      void queryClient.invalidateQueries({ queryKey: keys.tenant(tenantId) });
      void queryClient.invalidateQueries({ queryKey: keys.tenantAi(tenantId) });
      void queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    }
    previous.current = status;
  }, [status, tenantId, queryClient]);

  const retry = useMutation({
    mutationFn: () => platformApi.retryProvisioning(tenantId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.provisioning(tenantId) });
      await queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    },
  });

  const stopCopy = useMutation({
    mutationFn: (stopped: boolean) => platformApi.updateAiCopySettings(tenantId, { stopped }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(keys.tenantCopySummary(tenantId), updated);
      await queryClient.invalidateQueries({ queryKey: keys.tenantAi(tenantId) });
      await queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    },
  });

  return (
    <Card title={he.provisioning.title}>
      {provisioning.isPending ? (
        <Loading />
      ) : provisioning.isError ? (
        <Alert>{errorMessage(provisioning.error)}</Alert>
      ) : provisioning.data.run === null ? (
        <p className="text-sm text-slate-600">{he.provisioning.none}</p>
      ) : (
        <RunDetails
          run={provisioning.data.run}
          copy={
            showCopyProgress && waitsForContent(provisioning.data.run)
              ? (content.data?.products ?? null)
              : null
          }
          copyControl={
            canStopCopy && waitsForContent(provisioning.data.run) && copySummary.data !== undefined
              ? {
                  stopped: copySummary.data.stopped,
                  busy: stopCopy.isPending,
                  onClick: () => {
                    stopCopy.mutate(!copySummary.data.stopped);
                  },
                }
              : null
          }
          retry={
            canRetry && provisioning.data.run.retryable
              ? {
                  busy: retry.isPending,
                  onClick: () => {
                    retry.mutate();
                  },
                }
              : null
          }
        />
      )}
      {copySummary.isError ? <Alert>{errorMessage(copySummary.error)}</Alert> : null}
      {stopCopy.isError ? <Alert>{errorMessage(stopCopy.error)}</Alert> : null}
      {/* Only while the run waits for the copy: once the store opened the notice is history. */}
      {stopCopy.isSuccess && waitsForContent(provisioning.data?.run) ? (
        <Alert tone="success">
          {stopCopy.data.stopped ? he.aiCopy.stoppedNow : he.aiCopy.resumedNow}
        </Alert>
      ) : null}
      {retry.isError ? <Alert>{errorMessage(retry.error)}</Alert> : null}
      {retry.isSuccess ? <Alert tone="success">{he.provisioning.retried}</Alert> : null}
    </Card>
  );
}

function RunDetails({
  run,
  copy,
  copyControl,
  retry,
}: {
  run: ProvisioningRun;
  /** The products that have copy so far, while the run waits for the store's copy. */
  copy: TenantAiStatus['products'] | null;
  /** Stopping and continuing the store's copy writing, while the run waits for it. */
  copyControl: { stopped: boolean; busy: boolean; onClick: () => void } | null;
  retry: { busy: boolean; onClick: () => void } | null;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid="provisioning" data-status={run.status}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt className="text-slate-500">{he.provisioning.status}</dt>
        <dd>
          <Badge tone={runTones[run.status]}>{he.provisioning.runStatus[run.status]}</Badge>
        </dd>
        <dt className="text-slate-500">{he.provisioning.currentStep}</dt>
        <dd>{he.provisioning.steps[run.currentStep]}</dd>
        <dt className="text-slate-500">{he.provisioning.categories}</dt>
        <dd>{run.counts.categories}</dd>
        <dt className="text-slate-500">{he.provisioning.products}</dt>
        <dd data-testid="provisioning-products">{run.counts.products}</dd>
        <dt className="text-slate-500">{he.provisioning.startedAt}</dt>
        <dd>{formatDateTime(run.startedAt)}</dd>
        {run.finishedAt === null ? null : (
          <>
            <dt className="text-slate-500">{he.provisioning.finishedAt}</dt>
            <dd>{formatDateTime(run.finishedAt)}</dd>
          </>
        )}
      </dl>

      {copyControl?.stopped === true && run.waitingReason === 'CONTENT_IN_PROGRESS' ? (
        <div data-testid="provisioning-copy-stopped">
          <Alert tone="warning">{he.aiCopy.stopped}</Alert>
        </div>
      ) : run.waitingReason === null ? null : (
        <Alert tone="info">{he.provisioning.waiting[run.waitingReason]}</Alert>
      )}
      {copy === null ? null : (
        <div className="flex flex-col gap-1">
          <p className="text-sm text-slate-500">{he.provisioning.copyProgress}</p>
          <CopyProgress
            products={copy}
            label={he.provisioning.copyProgressMeter}
            testId="provisioning-copy-progress"
          />
        </div>
      )}
      {copyControl === null ? null : (
        <div>
          <Button
            tone={copyControl.stopped ? 'primary' : 'secondary'}
            data-testid={
              copyControl.stopped ? 'provisioning-copy-resume' : 'provisioning-copy-stop'
            }
            busy={copyControl.busy}
            onClick={copyControl.onClick}
          >
            {copyControl.stopped ? he.aiCopy.resume : he.aiCopy.stop}
          </Button>
        </div>
      )}
      {run.errorCode === null ? null : <Alert>{he.provisioning.errors[run.errorCode]}</Alert>}
      {run.stalled ? <Alert>{he.provisioning.stalled}</Alert> : null}

      <ol className="flex flex-col gap-2 text-sm">
        {run.steps.map((step, index) => (
          <li key={step.step} className="flex flex-wrap items-center gap-2">
            <span className="w-5 text-slate-400">{index + 1}.</span>
            <span className="flex-1">{he.provisioning.steps[step.step]}</span>
            {step.attempts > 1 ? (
              <span className="text-xs text-slate-500">
                {he.provisioning.attempts}: {step.attempts}
              </span>
            ) : null}
            <Badge tone={stepTones[step.status]}>{he.provisioning.stepStatus[step.status]}</Badge>
          </li>
        ))}
      </ol>

      {retry === null ? null : (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-slate-600">{he.provisioning.retryBody}</p>
          <div>
            <Button busy={retry.busy} onClick={retry.onClick}>
              {he.provisioning.retry}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
